import { useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, DatePicker, Divider, Input, Menu, Message, Modal, Panel, Popover, Stack, Whisper, toaster } from 'rsuite';
import API_CONFIG from '../config/apiConfig';
import useAuthenticatedFetch from '../hooks/useAuthenticatedFetch';

import {
  useCreateTaskOrderInvoice,
  useDeleteTaskOrderInvoice,
  useDeleteTaskOrderInvoicePdf,
  useTaskOrderInvoices,
  useUpdateTaskOrderInvoice,
  useUploadTaskOrderInvoicePdf,
} from '../hooks/useQueries';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const formatCurrency = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return currencyFormatter.format(n);
};

const normalizeCurrencyInput = (value) => {
  const raw = String(value || '').replace(/[^\d.]/g, '');
  if (!raw) return '';

  const dotIndex = raw.indexOf('.');
  const hasDot = dotIndex >= 0;
  const integerPartRaw = hasDot ? raw.slice(0, dotIndex) : raw;
  const decimalPartRaw = hasDot ? raw.slice(dotIndex + 1).replace(/\./g, '') : '';

  const integerDigits = integerPartRaw.replace(/^0+(?=\d)/, '') || '0';
  const integerFormatted = integerDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const decimalDigits = decimalPartRaw.slice(0, 2);

  if (hasDot) {
    return `$${integerFormatted}.${decimalDigits}`;
  }
  return `$${integerFormatted}`;
};

const parseCurrencyInput = (value) => {
  const raw = String(value || '').replace(/[^\d.]/g, '');
  if (!raw) return NaN;
  const n = Number(raw);
  return Number.isFinite(n) ? n : NaN;
};

const getTodayIsoDate = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const isoToDate = (iso) => {
  const value = String(iso || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const dt = new Date(`${value}T00:00:00`);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

const dateToIso = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return null;
  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, '0');
  const d = String(value.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

const MAX_INVOICE_PDF_BYTES = 10 * 1024 * 1024;

export default function TaskOrderInvoicesPage() {
  const navigate = useNavigate();
  const authenticatedFetch = useAuthenticatedFetch();
  const params = useParams();
  const projectId = Number(params?.projectId);
  const taskOrderId = Number(params?.taskOrderId);

  const {
    data: invoicesData,
    isLoading: invoicesLoading,
    error: invoicesError,
  } = useTaskOrderInvoices(projectId, taskOrderId, {
    enabled: Number.isInteger(projectId) && Number.isInteger(taskOrderId),
  });

  const createTaskOrderInvoice = useCreateTaskOrderInvoice();
  const deleteInvoice = useDeleteTaskOrderInvoice();
  const updateInvoice = useUpdateTaskOrderInvoice();
  const uploadInvoicePdf = useUploadTaskOrderInvoicePdf();
  const deleteInvoicePdf = useDeleteTaskOrderInvoicePdf();

  const invoiceRows = useMemo(() => {
    const rows = Array.isArray(invoicesData) ? [...invoicesData] : [];
    rows.sort((a, b) => {
      const aDate = String(a?.invoiceDate || '');
      const bDate = String(b?.invoiceDate || '');
      if (aDate !== bDate) return bDate.localeCompare(aDate);
      return Number(b?.id ?? 0) - Number(a?.id ?? 0);
    });
    return rows;
  }, [invoicesData]);

  const invoiceTotal = useMemo(
    () => invoiceRows.reduce((sum, row) => sum + Number(row?.invoiceLaborAmount ?? 0) + Number(row?.invoiceOdcAmount ?? 0), 0),
    [invoiceRows]
  );

  const [newInvoiceDate, setNewInvoiceDate] = useState(() => getTodayIsoDate());
  const [newInvoiceLaborAmount, setNewInvoiceLaborAmount] = useState('');
  const [newInvoiceOdcAmount, setNewInvoiceOdcAmount] = useState('');
  const [newInvoiceComment, setNewInvoiceComment] = useState('');
  const [newInvoiceFileLink, setNewInvoiceFileLink] = useState('');
  const [newInvoicePdfFile, setNewInvoicePdfFile] = useState(null);
  const [addInvoiceModalOpen, setAddInvoiceModalOpen] = useState(false);
  const [pdfOpeningInvoiceId, setPdfOpeningInvoiceId] = useState(null);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [invoiceToEdit, setInvoiceToEdit] = useState(null);
  const [editInvoiceDate, setEditInvoiceDate] = useState('');
  const [editInvoiceLaborAmount, setEditInvoiceLaborAmount] = useState('');
  const [editInvoiceOdcAmount, setEditInvoiceOdcAmount] = useState('');
  const [editInvoiceComment, setEditInvoiceComment] = useState('');
  const [editInvoiceFileLink, setEditInvoiceFileLink] = useState('');
  const [editInvoicePdfFile, setEditInvoicePdfFile] = useState(null);
  const createInvoicePdfInputRef = useRef(null);
  const editInvoicePdfInputRef = useRef(null);

  const newInvoiceRunningTotal = useMemo(() => {
    const labor = parseCurrencyInput(newInvoiceLaborAmount);
    const odc = parseCurrencyInput(newInvoiceOdcAmount);
    const laborSafe = Number.isFinite(labor) && labor >= 0 ? labor : 0;
    const odcSafe = Number.isFinite(odc) && odc >= 0 ? odc : 0;
    return laborSafe + odcSafe;
  }, [newInvoiceLaborAmount, newInvoiceOdcAmount]);

  const editInvoiceRunningTotal = useMemo(() => {
    const labor = parseCurrencyInput(editInvoiceLaborAmount);
    const odc = parseCurrencyInput(editInvoiceOdcAmount);
    const laborSafe = Number.isFinite(labor) && labor >= 0 ? labor : 0;
    const odcSafe = Number.isFinite(odc) && odc >= 0 ? odc : 0;
    return laborSafe + odcSafe;
  }, [editInvoiceLaborAmount, editInvoiceOdcAmount]);

  const openAddInvoiceModal = () => {
    setAddInvoiceModalOpen(true);
  };

  const closeAddInvoiceModal = () => {
    if (createTaskOrderInvoice.isPending || uploadInvoicePdf.isPending) return;
    setAddInvoiceModalOpen(false);
  };

  const submitAddInvoice = async () => {
    const invoiceDate = String(newInvoiceDate || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(invoiceDate)) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Invoice date is required.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    const laborAmount = parseCurrencyInput(newInvoiceLaborAmount);
    if (!Number.isFinite(laborAmount) || laborAmount < 0) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Labor amount must be a number greater than or equal to 0.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    const odcAmount = parseCurrencyInput(newInvoiceOdcAmount);
    if (!Number.isFinite(odcAmount) || odcAmount < 0) {
      toaster.push(
        <Message showIcon type="warning" closable>
          ODC amount must be a number greater than or equal to 0.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    const trimmedLink = String(newInvoiceFileLink || '').trim();
    if (trimmedLink) {
      try {
        const parsed = new URL(trimmedLink);
        if (!['http:', 'https:'].includes(String(parsed.protocol || '').toLowerCase())) {
          throw new Error('invalid protocol');
        }
      } catch {
        toaster.push(
          <Message showIcon type="warning" closable>
            File link must be a valid http(s) URL.
          </Message>,
          { placement: 'topEnd' }
        );
        return;
      }
    }

    if (newInvoicePdfFile) {
      if (String(newInvoicePdfFile.type || '').toLowerCase() !== 'application/pdf') {
        toaster.push(
          <Message showIcon type="warning" closable>
            Only PDF files are allowed.
          </Message>,
          { placement: 'topEnd' }
        );
        return;
      }

      if (Number(newInvoicePdfFile.size || 0) > MAX_INVOICE_PDF_BYTES) {
        toaster.push(
          <Message showIcon type="warning" closable>
            PDF too large. Max size is 10MB.
          </Message>,
          { placement: 'topEnd' }
        );
        return;
      }
    }

    try {
      const createdInvoice = await createTaskOrderInvoice.mutateAsync({
        projectId,
        taskOrderId,
        payload: {
          invoiceDate,
          invoiceLaborAmount: laborAmount,
          invoiceOdcAmount: odcAmount,
          invoiceComment: String(newInvoiceComment || '').trim() || null,
          invoiceFileLink: trimmedLink || null,
        },
      });

      if (newInvoicePdfFile && Number.isInteger(Number(createdInvoice?.id))) {
        try {
          const dataBase64 = await fileToBase64(newInvoicePdfFile);
          await uploadInvoicePdf.mutateAsync({
            projectId,
            taskOrderId,
            invoiceId: Number(createdInvoice.id),
            payload: {
              fileName: newInvoicePdfFile.name,
              mimeType: newInvoicePdfFile.type,
              dataBase64,
            },
          });
        } catch (uploadErr) {
          toaster.push(
            <Message showIcon type="warning" closable>
              Invoice was created, but PDF upload failed: {String(uploadErr?.message || uploadErr || 'Unknown error')}
            </Message>,
            { placement: 'topEnd' }
          );
        }
      }

      toaster.push(
        <Message showIcon type="success" closable>
          Invoice saved.
        </Message>,
        { placement: 'topEnd' }
      );

      setNewInvoiceLaborAmount('');
      setNewInvoiceOdcAmount('');
      setNewInvoiceComment('');
      setNewInvoiceFileLink('');
      setNewInvoicePdfFile(null);
      setNewInvoiceDate(getTodayIsoDate());
      setAddInvoiceModalOpen(false);
      if (createInvoicePdfInputRef.current) {
        createInvoicePdfInputRef.current.value = '';
      }
    } catch (e) {
      toaster.push(
        <Message showIcon type="error" closable>
          {String(e?.message || e || 'Failed to save invoice.')}
        </Message>,
        { placement: 'topEnd' }
      );
    }
  };

  const submitUploadPdf = async (invoice, selectedFile) => {
    const invoiceId = Number(invoice?.id);
    if (!Number.isInteger(invoiceId)) return;

    const selected = selectedFile || null;
    if (!selected) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Choose a PDF before uploading.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    if (String(selected.type || '').toLowerCase() !== 'application/pdf') {
      toaster.push(
        <Message showIcon type="warning" closable>
          Only PDF files are allowed.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    if (Number(selected.size || 0) > MAX_INVOICE_PDF_BYTES) {
      toaster.push(
        <Message showIcon type="warning" closable>
          PDF too large. Max size is 10MB.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    try {
      const dataBase64 = await fileToBase64(selected);
      await uploadInvoicePdf.mutateAsync({
        projectId,
        taskOrderId,
        invoiceId,
        payload: {
          fileName: selected.name,
          mimeType: selected.type,
          dataBase64,
        },
      });

      toaster.push(
        <Message showIcon type="success" closable>
          Invoice PDF uploaded.
        </Message>,
        { placement: 'topEnd' }
      );
    } catch (e) {
      toaster.push(
        <Message showIcon type="error" closable>
          {String(e?.message || e || 'Failed to upload invoice PDF.')}
        </Message>,
        { placement: 'topEnd' }
      );
    }
  };

  const promptAndUploadPdf = (invoice) => {
    const picker = document.createElement('input');
    picker.type = 'file';
    picker.accept = 'application/pdf';
    picker.onchange = async (event) => {
      const selected = event.target?.files?.[0] || null;
      if (!selected) return;
      await submitUploadPdf(invoice, selected);
    };
    picker.click();
  };

  const openInvoicePdf = async (invoice) => {
    const invoiceId = Number(invoice?.id);
    const pdfPath = String(invoice?.invoicePdfUrl || '').trim();
    if (!Number.isInteger(invoiceId) || !pdfPath) return;

    setPdfOpeningInvoiceId(invoiceId);
    try {
      let fullUrl = pdfPath;
      if (!/^https?:\/\//i.test(pdfPath)) {
        const base = String(API_CONFIG.BASE_URL || '').replace(/\/+$/, '');
        const normalizedPath = pdfPath.startsWith('/') ? pdfPath : `/${pdfPath}`;
        if (normalizedPath.startsWith('/api/')) {
          const origin = base.replace(/\/api$/i, '');
          fullUrl = `${origin}${normalizedPath}`;
        } else {
          fullUrl = `${base}${normalizedPath}`;
        }
      }

      const response = await authenticatedFetch(fullUrl, {
        method: 'GET',
        headers: { Accept: 'application/pdf' },
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Failed to open invoice PDF');
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60 * 1000);
    } catch (e) {
      toaster.push(
        <Message showIcon type="error" closable>
          {String(e?.message || e || 'Failed to open invoice PDF.')}
        </Message>,
        { placement: 'topEnd' }
      );
    } finally {
      setPdfOpeningInvoiceId(null);
    }
  };

  const submitDeletePdf = async (invoice) => {
    const invoiceId = Number(invoice?.id);
    if (!Number.isInteger(invoiceId)) return;

    try {
      await deleteInvoicePdf.mutateAsync({ projectId, taskOrderId, invoiceId });
      toaster.push(
        <Message showIcon type="success" closable>
          Invoice PDF deleted.
        </Message>,
        { placement: 'topEnd' }
      );
    } catch (e) {
      toaster.push(
        <Message showIcon type="error" closable>
          {String(e?.message || e || 'Failed to delete invoice PDF.')}
        </Message>,
        { placement: 'topEnd' }
      );
    }
  };

  const openDeleteInvoiceModal = (invoice) => {
    const invoiceId = Number(invoice?.id);
    if (!Number.isInteger(invoiceId)) return;

    setInvoiceToDelete({
      id: invoiceId,
      invoiceDate: String(invoice?.invoiceDate || ''),
      invoiceAmount: Number(invoice?.invoiceLaborAmount ?? 0) + Number(invoice?.invoiceOdcAmount ?? 0),
    });
  };

  const closeDeleteInvoiceModal = () => {
    if (deleteInvoice.isPending) return;
    setInvoiceToDelete(null);
  };

  const submitDeleteInvoice = async () => {
    const invoiceId = Number(invoiceToDelete?.id);
    if (!Number.isInteger(invoiceId)) return;

    try {
      await deleteInvoice.mutateAsync({ projectId, taskOrderId, invoiceId });
      setInvoiceToDelete(null);
      toaster.push(
        <Message showIcon type="success" closable>
          Invoice deleted.
        </Message>,
        { placement: 'topEnd' }
      );
    } catch (e) {
      toaster.push(
        <Message showIcon type="error" closable>
          {String(e?.message || e || 'Failed to delete invoice.')}
        </Message>,
        { placement: 'topEnd' }
      );
    }
  };

  const openEditInvoiceModal = (invoice) => {
    const invoiceId = Number(invoice?.id);
    if (!Number.isInteger(invoiceId)) return;

    setInvoiceToEdit({
      id: invoiceId,
      invoiceDate: String(invoice?.invoiceDate || ''),
      invoiceAmount: Number(invoice?.invoiceAmount ?? 0),
      invoicePdfFileName: String(invoice?.invoicePdfFileName || ''),
      hasInvoicePdf: Boolean(invoice?.invoicePdfUrl),
    });
    setEditInvoiceDate(String(invoice?.invoiceDate || '').slice(0, 10));
    setEditInvoiceLaborAmount(
      Number.isFinite(Number(invoice?.invoiceLaborAmount))
        ? normalizeCurrencyInput(String(invoice?.invoiceLaborAmount ?? 0))
        : ''
    );
    setEditInvoiceOdcAmount(
      Number.isFinite(Number(invoice?.invoiceOdcAmount))
        ? normalizeCurrencyInput(String(invoice?.invoiceOdcAmount ?? 0))
        : ''
    );
    setEditInvoiceComment(String(invoice?.invoiceComment || ''));
    setEditInvoiceFileLink(String(invoice?.invoiceFileLink || ''));
    setEditInvoicePdfFile(null);
    if (editInvoicePdfInputRef.current) {
      editInvoicePdfInputRef.current.value = '';
    }
  };

  const closeEditInvoiceModal = () => {
    if (updateInvoice.isPending || uploadInvoicePdf.isPending) return;
    setInvoiceToEdit(null);
  };

  const submitEditInvoice = async () => {
    const invoiceId = Number(invoiceToEdit?.id);
    if (!Number.isInteger(invoiceId)) return;

    const invoiceDate = String(editInvoiceDate || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(invoiceDate)) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Invoice date is required.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    const laborAmount = parseCurrencyInput(editInvoiceLaborAmount);
    if (!Number.isFinite(laborAmount) || laborAmount < 0) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Labor amount must be a number greater than or equal to 0.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    const odcAmount = parseCurrencyInput(editInvoiceOdcAmount);
    if (!Number.isFinite(odcAmount) || odcAmount < 0) {
      toaster.push(
        <Message showIcon type="warning" closable>
          ODC amount must be a number greater than or equal to 0.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    const trimmedLink = String(editInvoiceFileLink || '').trim();
    if (trimmedLink) {
      try {
        const parsed = new URL(trimmedLink);
        if (!['http:', 'https:'].includes(String(parsed.protocol || '').toLowerCase())) {
          throw new Error('invalid protocol');
        }
      } catch {
        toaster.push(
          <Message showIcon type="warning" closable>
            File link must be a valid http(s) URL.
          </Message>,
          { placement: 'topEnd' }
        );
        return;
      }
    }

    if (editInvoicePdfFile) {
      if (String(editInvoicePdfFile.type || '').toLowerCase() !== 'application/pdf') {
        toaster.push(
          <Message showIcon type="warning" closable>
            Only PDF files are allowed.
          </Message>,
          { placement: 'topEnd' }
        );
        return;
      }

      if (Number(editInvoicePdfFile.size || 0) > MAX_INVOICE_PDF_BYTES) {
        toaster.push(
          <Message showIcon type="warning" closable>
            PDF too large. Max size is 10MB.
          </Message>,
          { placement: 'topEnd' }
        );
        return;
      }
    }

    try {
      const updatedInvoice = await updateInvoice.mutateAsync({
        projectId,
        taskOrderId,
        invoiceId,
        payload: {
          invoiceDate,
          invoiceLaborAmount: laborAmount,
          invoiceOdcAmount: odcAmount,
          invoiceComment: String(editInvoiceComment || '').trim() || null,
          invoiceFileLink: trimmedLink || null,
        },
      });

      if (editInvoicePdfFile && Number.isInteger(Number(updatedInvoice?.id || invoiceId))) {
        try {
          const dataBase64 = await fileToBase64(editInvoicePdfFile);
          await uploadInvoicePdf.mutateAsync({
            projectId,
            taskOrderId,
            invoiceId,
            payload: {
              fileName: editInvoicePdfFile.name,
              mimeType: editInvoicePdfFile.type,
              dataBase64,
            },
          });
        } catch (uploadErr) {
          toaster.push(
            <Message showIcon type="warning" closable>
              Invoice updated, but PDF upload failed: {String(uploadErr?.message || uploadErr || 'Unknown error')}
            </Message>,
            { placement: 'topEnd' }
          );
        }
      }

      setInvoiceToEdit(null);
      setEditInvoicePdfFile(null);
      if (editInvoicePdfInputRef.current) {
        editInvoicePdfInputRef.current.value = '';
      }

      toaster.push(
        <Message showIcon type="success" closable>
          Invoice updated.
        </Message>,
        { placement: 'topEnd' }
      );
    } catch (e) {
      toaster.push(
        <Message showIcon type="error" closable>
          {String(e?.message || e || 'Failed to update invoice.')}
        </Message>,
        { placement: 'topEnd' }
      );
    }
  };

  if (!Number.isInteger(projectId) || !Number.isInteger(taskOrderId)) {
    return (
      <div className="ppc-page">
        <Message type="error" showIcon>
          Invalid project/task order.
        </Message>
      </div>
    );
  }

  return (
    <div className="ppc-page">
      <Stack justifyContent="space-between" alignItems="center" wrap>
        <h3 style={{ margin: 0 }}>Task Order Invoices</h3>
        <Stack spacing={10} wrap>
          <Button appearance="ghost" size="sm" onClick={() => navigate(`/projects/${projectId}/task-orders/${taskOrderId}`)}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <i className="bi bi-arrow-left" aria-hidden="true" />
              Back to task order
            </span>
          </Button>
          <Button
            appearance="ghost"
            size="sm"
            onClick={() => navigate(`/projects/${projectId}/task-orders/${taskOrderId}/actuals`)}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              Actuals
              <i className="bi bi-clipboard-data" aria-hidden="true" />
            </span>
          </Button>
        </Stack>
      </Stack>

      <Divider />

      <Panel bordered header={<span>Invoices</span>}>
        <div style={{ display: 'grid', gap: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              appearance="primary"
              onClick={openAddInvoiceModal}
              disabled={createTaskOrderInvoice.isPending || uploadInvoicePdf.isPending}
            >
              Add Invoice
            </Button>
          </div>

          {invoicesError ? (
            <Message type="error" showIcon>
              Failed to load invoices.
            </Message>
          ) : null}

          {invoicesLoading ? <div>Loading invoices…</div> : null}

          {!invoicesLoading && !invoicesError && invoiceRows.length === 0 ? (
            <Message type="info" showIcon>
              No invoices yet for this task order.
            </Message>
          ) : null}

          {!invoicesLoading && !invoicesError && invoiceRows.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '8px 6px', borderBottom: '1px solid #e6e6e6' }}>Date</th>
                    <th style={{ textAlign: 'right', padding: '8px 6px', borderBottom: '1px solid #e6e6e6' }}>Labor</th>
                    <th style={{ textAlign: 'right', padding: '8px 6px', borderBottom: '1px solid #e6e6e6' }}>ODCs</th>
                    <th style={{ textAlign: 'right', padding: '8px 6px', borderBottom: '1px solid #e6e6e6' }}>Total</th>
                    <th style={{ textAlign: 'center', padding: '8px 6px', borderBottom: '1px solid #e6e6e6' }}>Comment</th>
                    <th style={{ textAlign: 'left', padding: '8px 6px', borderBottom: '1px solid #e6e6e6' }}>File</th>
                    <th style={{ textAlign: 'left', padding: '8px 6px', borderBottom: '1px solid #e6e6e6' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoiceRows.map((row) => {
                    const invoiceId = Number(row?.id);
                    const rowTotal = Number(row?.invoiceLaborAmount ?? 0) + Number(row?.invoiceOdcAmount ?? 0);
                    return (
                      <tr key={invoiceId}>
                        <td style={{ padding: '8px 6px', borderBottom: '1px solid #f0f0f0' }}>
                          {String(row?.invoiceDate || '—')}
                        </td>
                        <td style={{ padding: '8px 6px', borderBottom: '1px solid #f0f0f0', textAlign: 'right' }}>
                          {formatCurrency(Number(row?.invoiceLaborAmount ?? 0))}
                        </td>
                        <td style={{ padding: '8px 6px', borderBottom: '1px solid #f0f0f0', textAlign: 'right' }}>
                          {formatCurrency(Number(row?.invoiceOdcAmount ?? 0))}
                        </td>
                        <td style={{ padding: '8px 6px', borderBottom: '1px solid #f0f0f0', textAlign: 'right' }}>
                          {formatCurrency(rowTotal)}
                        </td>
                        <td style={{ padding: '8px 6px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}>
                          {String(row?.invoiceComment || '—')}
                        </td>
                        <td style={{ padding: '8px 6px', borderBottom: '1px solid #f0f0f0' }}>
                          <Stack spacing={8} direction="column" alignItems="flex-start">
                            {row?.invoiceFileLink ? (
                              <a href={row.invoiceFileLink} target="_blank" rel="noreferrer">
                                Open Link
                              </a>
                            ) : null}
                            {row?.invoicePdfUrl ? (
                              <Button
                                appearance="link"
                                size="xs"
                                onClick={() => openInvoicePdf(row)}
                                loading={pdfOpeningInvoiceId === invoiceId}
                                disabled={pdfOpeningInvoiceId === invoiceId}
                              >
                                {String(row?.invoicePdfFileName || 'Preview PDF')}
                              </Button>
                            ) : null}
                            {!row?.invoiceFileLink && !row?.invoicePdfUrl ? '—' : null}
                          </Stack>
                        </td>
                        <td style={{ padding: '8px 6px', borderBottom: '1px solid #f0f0f0' }}>
                          <Whisper
                            trigger="click"
                            placement="bottomEnd"
                            container={() => document.body}
                            speaker={(speakerProps, ref) => {
                              const { onClose, className, style, ...rest } = speakerProps;
                              return (
                                <Popover {...rest} ref={ref} className={className} full style={{ ...style, zIndex: 99999 }}>
                                  <Menu
                                    onSelect={(eventKey) => {
                                      onClose?.();
                                      if (eventKey === 'edit') {
                                        openEditInvoiceModal(row);
                                        return;
                                      }
                                      if (eventKey === 'upload') {
                                        promptAndUploadPdf(row);
                                        return;
                                      }
                                      if (eventKey === 'delete-pdf') {
                                        submitDeletePdf(row);
                                        return;
                                      }
                                      if (eventKey === 'delete-invoice') {
                                        openDeleteInvoiceModal(row);
                                      }
                                    }}
                                  >
                                    <Menu.Item
                                      eventKey="edit"
                                      disabled={updateInvoice.isPending || deleteInvoice.isPending}
                                    >
                                      Edit invoice
                                    </Menu.Item>
                                    <Menu.Item eventKey="upload" disabled={uploadInvoicePdf.isPending}>
                                      {row?.invoicePdfUrl ? 'Replace PDF' : 'Upload PDF'}
                                    </Menu.Item>
                                    {row?.invoicePdfUrl ? (
                                      <Menu.Item eventKey="delete-pdf" disabled={deleteInvoicePdf.isPending}>
                                        Delete PDF
                                      </Menu.Item>
                                    ) : null}
                                    <Menu.Item eventKey="delete-invoice" disabled={deleteInvoice.isPending}>
                                      Delete invoice
                                    </Menu.Item>
                                  </Menu>
                                </Popover>
                              );
                            }}
                          >
                            <Button
                              appearance="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              aria-label="Invoice actions"
                            >
                              View actions
                            </Button>
                          </Whisper>
                        </td>
                      </tr>
                    );
                  })}
                  <tr>
                    <td style={{ padding: '10px 6px', borderTop: '2px solid #e6e6e6', fontWeight: 800 }}>Total invoiced</td>
                    <td style={{ padding: '10px 6px', borderTop: '2px solid #e6e6e6', textAlign: 'right', fontWeight: 800 }}>
                      {formatCurrency(invoiceRows.reduce((sum, row) => sum + Number(row?.invoiceLaborAmount ?? 0), 0))}
                    </td>
                    <td style={{ padding: '10px 6px', borderTop: '2px solid #e6e6e6', textAlign: 'right', fontWeight: 800 }}>
                      {formatCurrency(invoiceRows.reduce((sum, row) => sum + Number(row?.invoiceOdcAmount ?? 0), 0))}
                    </td>
                    <td style={{ padding: '10px 6px', borderTop: '2px solid #e6e6e6', textAlign: 'right', fontWeight: 800 }}>
                      {formatCurrency(invoiceTotal)}
                    </td>
                    <td style={{ padding: '10px 6px', borderTop: '2px solid #e6e6e6' }} />
                    <td style={{ padding: '10px 6px', borderTop: '2px solid #e6e6e6' }} />
                    <td style={{ padding: '10px 6px', borderTop: '2px solid #e6e6e6' }} />
                  </tr>
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </Panel>

      <Modal open={Boolean(invoiceToDelete)} onClose={closeDeleteInvoiceModal} size="xs">
        <Modal.Header>
          <Modal.Title>Delete invoice?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Message showIcon type="warning" style={{ marginBottom: 12 }}>
            This cannot be undone.
          </Message>
          <div>
            {invoiceToDelete
              ? `Invoice ${invoiceToDelete.invoiceDate || '—'} • ${formatCurrency(invoiceToDelete.invoiceAmount)}`
              : ''}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            appearance="primary"
            color="red"
            onClick={submitDeleteInvoice}
            loading={deleteInvoice.isPending}
            disabled={deleteInvoice.isPending}
          >
            Delete Invoice
          </Button>
          <Button appearance="subtle" onClick={closeDeleteInvoiceModal} disabled={deleteInvoice.isPending}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal open={addInvoiceModalOpen} onClose={closeAddInvoiceModal} size="md">
        <Modal.Header>
          <Modal.Title>Add invoice</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'grid', gap: 12 }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: 10,
                alignItems: 'end',
              }}
            >
              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Invoice date</div>
                <DatePicker
                  oneTap
                  cleanable={false}
                  format="MM/dd/yyyy"
                  value={isoToDate(newInvoiceDate)}
                  onChange={(value) => setNewInvoiceDate(dateToIso(value) || '')}
                  style={{ width: '100%' }}
                  disabled={createTaskOrderInvoice.isPending || uploadInvoicePdf.isPending}
                />
              </div>

              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Labor amount</div>
                <Input
                  type="text"
                  value={newInvoiceLaborAmount}
                  onChange={(v) => setNewInvoiceLaborAmount(normalizeCurrencyInput(v))}
                  placeholder="$0.00"
                  disabled={createTaskOrderInvoice.isPending || uploadInvoicePdf.isPending}
                />
              </div>

              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>ODC amount</div>
                <Input
                  type="text"
                  value={newInvoiceOdcAmount}
                  onChange={(v) => setNewInvoiceOdcAmount(normalizeCurrencyInput(v))}
                  placeholder="$0.00"
                  disabled={createTaskOrderInvoice.isPending || uploadInvoicePdf.isPending}
                />
              </div>
            </div>

            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Comment (optional)</div>
              <Input
                as="textarea"
                rows={3}
                value={newInvoiceComment}
                onChange={(v) => setNewInvoiceComment(String(v || ''))}
                placeholder="Add context for this invoice..."
                disabled={createTaskOrderInvoice.isPending || uploadInvoicePdf.isPending}
              />
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 10,
                alignItems: 'end',
              }}
            >
              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>File link (optional, e.g. SharePoint)</div>
                <Input
                  type="text"
                  value={newInvoiceFileLink}
                  onChange={(v) => setNewInvoiceFileLink(String(v || ''))}
                  placeholder="https://..."
                  disabled={createTaskOrderInvoice.isPending || uploadInvoicePdf.isPending}
                />
              </div>

              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Invoice PDF (optional)</div>
                <input
                  ref={createInvoicePdfInputRef}
                  type="file"
                  accept="application/pdf"
                  onChange={(event) => {
                    const file = event.target?.files?.[0] || null;
                    setNewInvoicePdfFile(file);
                  }}
                  disabled={createTaskOrderInvoice.isPending || uploadInvoicePdf.isPending}
                />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 6 }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 12, color: '#666' }}>Invoice total</div>
                <div style={{ fontSize: 22, fontWeight: 700 }}>{formatCurrency(newInvoiceRunningTotal)}</div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            appearance="primary"
            onClick={submitAddInvoice}
            loading={createTaskOrderInvoice.isPending || uploadInvoicePdf.isPending}
            disabled={createTaskOrderInvoice.isPending || uploadInvoicePdf.isPending}
          >
            Add Invoice
          </Button>
          <Button
            appearance="subtle"
            onClick={closeAddInvoiceModal}
            disabled={createTaskOrderInvoice.isPending || uploadInvoicePdf.isPending}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal open={Boolean(invoiceToEdit)} onClose={closeEditInvoiceModal} size="md">
        <Modal.Header>
          <Modal.Title>Edit invoice</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'grid', gap: 12 }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(140px, 1fr))',
                gap: 10,
                alignItems: 'end',
              }}
            >
              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Invoice date</div>
                <DatePicker
                  oneTap
                  cleanable={false}
                  format="MM/dd/yyyy"
                  value={isoToDate(editInvoiceDate)}
                  onChange={(value) => setEditInvoiceDate(dateToIso(value) || '')}
                  style={{ width: '100%' }}
                  disabled={updateInvoice.isPending}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Labor amount</div>
                <Input
                  type="text"
                  value={editInvoiceLaborAmount}
                  onChange={(v) => setEditInvoiceLaborAmount(normalizeCurrencyInput(v))}
                  placeholder="$0.00"
                  disabled={updateInvoice.isPending}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>ODC amount</div>
                <Input
                  type="text"
                  value={editInvoiceOdcAmount}
                  onChange={(v) => setEditInvoiceOdcAmount(normalizeCurrencyInput(v))}
                  placeholder="$0.00"
                  disabled={updateInvoice.isPending}
                />
              </div>
            </div>

            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Comment (optional)</div>
              <Input
                as="textarea"
                rows={3}
                value={editInvoiceComment}
                onChange={(v) => setEditInvoiceComment(String(v || ''))}
                placeholder="Add context for this invoice..."
                disabled={updateInvoice.isPending}
              />
            </div>

            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>File link (optional)</div>
              <Input
                type="text"
                value={editInvoiceFileLink}
                onChange={(v) => setEditInvoiceFileLink(String(v || ''))}
                placeholder="https://..."
                disabled={updateInvoice.isPending || uploadInvoicePdf.isPending}
              />
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 10,
                alignItems: 'end',
              }}
            >
              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>
                  Invoice PDF (optional {invoiceToEdit?.hasInvoicePdf ? 'replace current' : 'add'})
                </div>
                <input
                  ref={editInvoicePdfInputRef}
                  type="file"
                  accept="application/pdf"
                  onChange={(event) => {
                    const file = event.target?.files?.[0] || null;
                    setEditInvoicePdfFile(file);
                  }}
                  disabled={updateInvoice.isPending || uploadInvoicePdf.isPending}
                />
                {invoiceToEdit?.hasInvoicePdf ? (
                  <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                    Current: {String(invoiceToEdit?.invoicePdfFileName || 'PDF attached')}
                  </div>
                ) : null}
              </div>

              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 12, color: '#666' }}>Invoice total</div>
                <div style={{ fontSize: 22, fontWeight: 700 }}>{formatCurrency(editInvoiceRunningTotal)}</div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            appearance="primary"
            onClick={submitEditInvoice}
            loading={updateInvoice.isPending || uploadInvoicePdf.isPending}
            disabled={updateInvoice.isPending || uploadInvoicePdf.isPending}
          >
            Save Changes
          </Button>
          <Button
            appearance="subtle"
            onClick={closeEditInvoiceModal}
            disabled={updateInvoice.isPending || uploadInvoicePdf.isPending}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
